import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { AppConstants } from '@apps/app.constants';
import { IAuroraState } from '@apps/aurora.state';
import * as fromAuthStore from '@apps/auth/store';
import { TokenService } from '@apps/auth/api/token.service';

import { marked } from 'marked';

type ChatRole = 'bot' | 'user';

interface ChatMessage {
    role: ChatRole;
    text: string;
    html?: string;
}

interface CoreAiStep {
    id?: number;
    type?: string;
    role?: string;
    content?: string;
}

interface CoreAiConversationResponse {
    id?: number;
    steps?: CoreAiStep[];
}

@Component({
    selector: 'aa-chat-bot-widget',
    templateUrl: './chat-bot-widget.component.html',
    styleUrls: ['./chat-bot-widget.component.scss'],
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatBotWidgetComponent implements AfterViewInit, OnDestroy {
    @ViewChild('messagesEl') private messagesEl?: ElementRef<HTMLElement>;
    @ViewChild('inputEl') private inputEl?: ElementRef<HTMLInputElement>;

    readonly primaryColor = '233 100% 13%';
    readonly secondaryColor = '121 29% 52%';
    readonly welcome = 'Comment puis-je vous aider ?';
    readonly logoUrl = 'https://aa.antemeta.ai/api/branding/logo';
    readonly showLogo = true;

    agentName: string | null = null;
    isLoggedIn = false;

    isOpen = false;
    inputDisabled = false;
    sending = false;

    inputValue = '';

    messages: ChatMessage[] = [];

    private conversationId: string | null = sessionStorage.getItem('smart_chatbot_conv_id');
    private readonly destroyed$ = new Subject<void>();

    constructor(
        private store: Store<IAuroraState>,
        private tokenService: TokenService,
        private cdr: ChangeDetectorRef
    ) { }

    ngAfterViewInit(): void {

        const isLoggedIn$ = this.store.select(fromAuthStore.getProfile).pipe(
            map((profile) => profile !== null),
            distinctUntilChanged()
        );

        const agentName$ = combineLatest([
            isLoggedIn$,
            this.store.select(fromAuthStore.getProfile),
            this.store.select(fromAuthStore.getBackAuthorization)
        ]).pipe(
            map(([loggedIn, profile, isAdmin]) => {
                if (!loggedIn) return null;
                if (profile === null) return null;
                return isAdmin ? 'MyAntemetAAdmin' : 'MyAntemetA';
            }),
            distinctUntilChanged()
        );

        combineLatest([isLoggedIn$, agentName$])
            .pipe(takeUntil(this.destroyed$))
            .subscribe(([loggedIn, agentName]) => {
                this.isLoggedIn = loggedIn;

                if (!loggedIn) {
                    this.resetWidgetState();
                    return;
                }

                if (agentName === null) {
                    this.agentName = null;
                    this.resetMessagesOnly();
                    return;
                }

                const agentChanged = this.agentName !== agentName;
                this.agentName = agentName;

                if (agentChanged) {
                    this.resetMessagesOnly();
                }

                if (this.messages.length === 0) {
                    this.addBotMessage(this.welcome);
                }

                this.cdr.markForCheck();
            });
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    get chatbotName(): string {
        return this.agentName ? `MyChaTbot ${this.agentName}` : 'MyChaTbot';
    }

    get isVisible(): boolean {
        return this.isLoggedIn && this.agentName !== null;
    }

    toggleOpen(): void {
        if (!this.isVisible) return;

        this.isOpen = !this.isOpen;
        if (this.isOpen) {
            setTimeout(() => this.inputEl?.nativeElement?.focus(), 300);
        }

        this.cdr.markForCheck();
    }

    close(event: Event): void {
        event.stopPropagation();
        this.isOpen = false;
        this.cdr.markForCheck();
    }

    onInput(value: string): void {
        this.inputValue = value;
        this.cdr.markForCheck();
    }

    onEnter(event: KeyboardEvent): void {
        if (event.key === 'Enter') {
            this.sendMessage();
        }
    }

    async sendMessage(): Promise<void> {
        if (!this.isVisible || this.inputDisabled || this.sending) return;

        const text = (this.inputValue || '').trim();
        if (!text) return;

        this.inputValue = '';
        this.addUserMessage(text);

        const botMsgIndex = this.addBotMessage('...');

        this.sending = true;
        this.cdr.markForCheck();
        let fullText = '';
        let buffer = '';

        try {
            const url = `${AppConstants.Configuration.apiUrlAuth}/auth/Chatbot`;
            const accessToken = this.tokenService.getAccessToken();

            const headers: Record<string, string> = {
                'Content-Type': 'application/json'
            };

            if (accessToken) {
                headers['Authorization'] = `Bearer ${accessToken}`;
            }

            const response = await fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify({
                    message: text,
                    agentName: this.agentName,
                    conversationId: this.conversationId,
                    stream: false
                })
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({} as any));
                throw new Error(errorData?.error || `Erreur serveur (${response.status})`);
            }

            const newConvId = response.headers.get('X-Conversation-Id');
            if (newConvId) {
                this.conversationId = newConvId;
                sessionStorage.setItem('smart_chatbot_conv_id', newConvId);
            }

            if (!response.body) throw new Error('Pas de flux de réponse reçu.');

            const reader = response.body.getReader();
            const decoder = new TextDecoder();

            this.updateBotMessage(botMsgIndex, '', true);

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: false });
                const lines = buffer.split('\n');
                buffer = lines.pop() || '';

                for (const line of lines) {
                    const trimmed = line.trim().replace(/^data:\s*/i, '');
                    if (!trimmed) continue;

                    try {
                        const data = JSON.parse(trimmed);
                        const next = this.extractAgentMessageText(data);
                        if (next) {
                            fullText = next;
                            this.updateBotMessage(botMsgIndex, fullText);
                        }
                    } catch {
                        // ignore parse errors
                    }
                }
            }

            // Handle non-stream JSON responses (single JSON object with no newlines)
            if (!fullText && buffer.trim()) {
                try {
                    const data = JSON.parse(buffer.trim());
                    const next = this.extractAgentMessageText(data);
                    if (next) {
                        fullText = next;
                        this.updateBotMessage(botMsgIndex, fullText);
                    }
                } catch {
                    // ignore
                }
            }
        } catch (err: any) {
            const base = AppConstants.Configuration.apiUrlAuth;
            this.updateBotMessage(botMsgIndex, `Erreur (${base}): ${err?.message ?? err}`, false);
        } finally {
            this.sending = false;
            this.cdr.markForCheck();
        }
    }

    private extractAgentMessageText(payload: any): string | null {
        if (!payload) return null;

        // New API example: full conversation payload with steps[]
        const conv = payload as CoreAiConversationResponse;
        const steps = Array.isArray(conv.steps) ? conv.steps : null;
        if (steps && steps.length > 0) {
            const lastAgentMessage = [...steps]
                .reverse()
                .find((s) => (s?.role === 'agent') && typeof s?.content === 'string' && s.content.trim().length > 0);

            if (lastAgentMessage?.content) {
                return lastAgentMessage.content;
            }
        }

        // Backwards/alternate streaming formats: { content: "..." }
        if (typeof payload.content === 'string' && payload.content.trim().length > 0) {
            return payload.content;
        }

        // Some stream formats might send step objects
        if (typeof payload.step?.content === 'string' && payload.step.content.trim().length > 0) {
            return payload.step.content;
        }

        return null;
    }

    private resetMessagesOnly(): void {
        this.messages = [];
        this.isOpen = false;
        this.inputDisabled = false;
        this.sending = false;
        this.inputValue = '';
        this.cdr.markForCheck();
    }

    private resetWidgetState(): void {
        this.agentName = null;
        this.resetMessagesOnly();
        this.cdr.markForCheck();
    }

    private addUserMessage(text: string): void {
        this.messages = [...this.messages, { role: 'user', text }];
        this.scrollToBottom();
        this.cdr.markForCheck();
    }

    private addBotMessage(text: string): number {
        const message: ChatMessage = {
            role: 'bot',
            text,
            html: this.renderMarkdown(text)
        };

        const next = [...this.messages, message];
        this.messages = next;
        this.scrollToBottom();
        this.cdr.markForCheck();
        return next.length - 1;
    }

    private updateBotMessage(index: number, text: string, isLoading: boolean = false): void {
        const updated = [...this.messages];
        const current = updated[index];
        if (!current || current.role !== 'bot') return;

        const nextText = isLoading ? text : text;
        updated[index] = {
            ...current,
            text: nextText,
            html: this.renderMarkdown(nextText)
        };
        this.messages = updated;
        this.scrollToBottom();
        this.cdr.markForCheck();
    }

    private renderMarkdown(text: string): string {
        try {
            const rendered = marked.parse(text ?? '');
            if (typeof rendered === 'string') {
                return rendered;
            }

            return (text ?? '').replace(/\n/g, '<br>');
        } catch {
            return (text ?? '').replace(/\n/g, '<br>');
        }
    }

    private scrollToBottom(): void {
        queueMicrotask(() => {
            const el = this.messagesEl?.nativeElement;
            if (!el) return;
            el.scrollTop = el.scrollHeight;
        });
    }
}
